import { useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { logWarnWrapper } from '../../../../../services/logWrapper';
import {
  setHotelContentRequested,
  setHotelContentReceived,
  setHotelContentFailed,
} from '../../../actions/michelin-actions';
import { COMPONENT_CAPYBARA_RECOMMENDATIONS } from '../../../helpers/constants/analyticsConstants';
import { getLocalisationVars } from '../../../selectors';
import { getHotelContent } from '../../clients/hotelContent';

const HOTEL_CONTENT_TIMEOUT_MS = 10_000;

export const useHotelContent = (hotelId: string | undefined) => {
  const dispatch = useDispatch();
  const { currency, locale, market } = useSelector(getLocalisationVars);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!hotelId) return undefined;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      abortController.abort();
    }, HOTEL_CONTENT_TIMEOUT_MS);

    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    const isStale = () => currentRequestId !== requestIdRef.current;

    dispatch(setHotelContentRequested());

    const doFetch = async () => {
      try {
        const hotel = await getHotelContent(
          hotelId,
          locale,
          market,
          currency,
          abortController.signal,
        );

        if (isStale()) return;

        if (hotel) {
          dispatch(setHotelContentReceived(hotel));
        } else {
          dispatch(setHotelContentFailed());
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          if (timedOut) {
            dispatch(setHotelContentFailed());
            logWarnWrapper({
              message: 'useHotelContent timeout',
              component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
              hotelId,
            });
          }
          return;
        }
        if (isStale()) return;

        dispatch(setHotelContentFailed());

        logWarnWrapper({
          message: 'useHotelContent fetch error',
          component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
          hotelId,
        });
      } finally {
        clearTimeout(timeoutId);
      }
    };

    doFetch();

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [hotelId, locale, market, currency, dispatch]);
};
